// useProfileFormLogic.ts
import { useState, useEffect, useCallback } from "react";

import { UseFetchInformationWithGemini } from "0.Mine/1.Hooks/AI/Gemini";
import { Business } from "0.Mine/3.Interfaces/business";

export interface ProfileFormLogicProps {
  website: string;
  updateBusinessData: (data: Partial<Business>) => void;
  businessData: Partial<Business>;
}

export interface ProfileFormLogicReturn {
  description: string;
  email: string;
  setDescription: (value: string) => void;
  setEmail: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
}

export const useProfileFormLogic = ({
  website,
  updateBusinessData,
  businessData,
}: ProfileFormLogicProps): ProfileFormLogicReturn => {
  // Local state for profile fields.
  const [description, setDescription] = useState<string>(businessData.about || "");
  const [email, setEmail] = useState<string>(businessData.email || "");
  // Flag to ensure data is fetched only once.
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [phone, setPhone] = useState<string>(businessData.phoneNumber || "");
  const { fetchInfromationWithGemini } = UseFetchInformationWithGemini();

  // Update the central business state whenever local fields change.
  useEffect(() => {
    updateBusinessData({ about: description, email });
  }, [description, email, updateBusinessData]);

  // Memoized fetch function so it isn't recreated on every render.
  const fetchData = useCallback(async () => {
    if (!website) return;
    try {
      const response = await fetchInfromationWithGemini(website);
      console.log(response);
      // Update local state if response contains profile data.
      if (response) {
        if (response.content) {
          setDescription(response.content);
        }
        if (response.contacts) {
          setEmail(response.contacts);
        }
      }
    } catch (error) {
      console.error("ERROR IN PROFILEFORM.TSX >>> ", error);
    }
  }, [website, fetchInfromationWithGemini]);

  // Fetch data only once when the website changes.
  useEffect(() => {
    if (website && !hasFetched) {
      fetchData();
      setHasFetched(true);
    }
  }, [website, hasFetched, fetchData]);

  return { description, email, setDescription, setEmail, phone, setPhone };
};
